import React, { useState, useContext } from "react";
import { Alert, Button, Box, Chip } from "@mui/material";
import { AdminPanelSettings, ExitToApp } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../services/use-auth";
import { tracedCallable } from "../utilities/tracedCallable";
import UserContext from "../assets/user_context";

/**
 * ControlBanner Component
 *
 * Displays a prominent banner when a T3Admin is actively controlling an organization.
 * Shows which org(s) they're controlling and provides quick release functionality.
 *
 * SECURITY: This is a critical UI indicator that admins are in control mode
 * Uses global controlSessions state from context for real-time updates
 */
const ControlBanner = () => {
  const { controlSessions, setOrg } = useContext(UserContext);
  const [releasing, setReleasing] = useState(null); // Track which session is being released
  const { functions } = useAuth();
  const theme = useTheme();

  const handleReleaseControl = async (orgId, orgName) => {
    if (!functions) return;

    setReleasing(orgId);
    try {
      const releaseControlOfOrg = tracedCallable(
        functions,
        "releaseControlOfOrg",
      );
      const result = await releaseControlOfOrg({ orgId });

      if (result.data.success) {
        console.log(`✅ Released control of ${orgName}`);
        // Clear org selection to show org selector modal
        setOrg(null);
        // Control sessions update automatically via real-time listener
      }
    } catch (error) {
      console.error(`❌ Failed to release control of ${orgName}:`, error);
      alert(`Failed to release control: ${error.message}`);
    } finally {
      setReleasing(null);
    }
  };

  const handleReleaseAll = async () => {
    if (!functions || controlSessions.length === 0) return;

    // Release all sessions in parallel
    setReleasing("all");
    try {
      const releaseControlOfOrg = tracedCallable(
        functions,
        "releaseControlOfOrg",
      );

      await Promise.all(
        controlSessions.map((session) =>
          releaseControlOfOrg({ orgId: session.orgId }),
        ),
      );

      console.log("✅ Released all control sessions");
      // Clear org selection to show org selector modal
      setOrg(null);
      // Control sessions update automatically via real-time listener
    } catch (error) {
      console.error("❌ Failed to release all control sessions:", error);
      alert(`Failed to release all sessions: ${error.message}`);
    } finally {
      setReleasing(null);
    }
  };

  // Don't show banner if no active sessions
  if (controlSessions.length === 0) {
    return null;
  }

  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        width: "100%",
        borderBottom: `2px solid ${theme.palette.warning.main}`,
        backgroundColor: isDark
          ? `${theme.palette.warning.dark}30`
          : theme.palette.warning.light,
      }}>
      <Alert
        severity="warning"
        icon={<AdminPanelSettings />}
        sx={{
          borderRadius: 0,
          alignItems: "center",
          py: 1.5,
          px: 3,
          "& .MuiAlert-message": {
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          },
          backgroundColor: "transparent",
          color: isDark ? theme.palette.warning.light : theme.palette.warning.contrastText,
        }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <strong>T3 ADMIN MODE:</strong>
            <span>You are controlling</span>
            {controlSessions.length === 1 ? (
              <Chip
                label={controlSessions[0].orgName}
                size="small"
                sx={{
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  fontWeight: "bold",
                }}
              />
            ) : (
              <Chip
                label={`${controlSessions.length} organizations`}
                size="small"
                sx={{
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.warning.contrastText,
                  fontWeight: "bold",
                }}
              />
            )}
          </Box>

          {/* Show individual orgs if multiple */}
          {controlSessions.length > 1 && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {controlSessions.map((session) => (
                <Chip
                  key={session.orgId}
                  label={session.orgName}
                  size="small"
                  onDelete={() =>
                    handleReleaseControl(session.orgId, session.orgName)
                  }
                  disabled={releasing === session.orgId || releasing === "all"}
                  sx={{
                    backgroundColor: isDark
                      ? theme.palette.warning.dark
                      : "rgba(255, 255, 255, 0.7)",
                    color: theme.palette.text.primary,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          {controlSessions.length === 1 ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<ExitToApp />}
              onClick={() =>
                handleReleaseControl(
                  controlSessions[0].orgId,
                  controlSessions[0].orgName,
                )
              }
              disabled={releasing === controlSessions[0].orgId}
              sx={{
                backgroundColor: theme.palette.warning.dark,
                color: "#ffffff",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: theme.palette.error.main,
                },
              }}>
              {releasing === controlSessions[0].orgId
                ? "Releasing..."
                : "Release Control"}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<ExitToApp />}
              onClick={handleReleaseAll}
              disabled={releasing === "all"}
              sx={{
                backgroundColor: theme.palette.warning.dark,
                color: "#ffffff",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: theme.palette.error.main,
                },
              }}>
              {releasing === "all" ? "Releasing..." : "Release All"}
            </Button>
          )}
        </Box>
      </Alert>
    </Box>
  );
};

export default ControlBanner;
